/**
 * `<caos-trace-flag-control>` — the trace flags in force, edited in the grid, with expiry told the truth about.
 * (Catalog: Developer surfaces › TraceFlagControl — the trace flags in force, with their expiry and levels editable in the row.)
 *
 * AN EXPIRED FLAG IS NOT A FLAG. Trace flags expire, and a surface that lists an expired one beside
 * a live one is the reason somebody reproduces a problem three times and gets no log. So expiry is
 * computed against a clock the caller supplies, said in words on the row, and counted separately in
 * the summary — never left as a date the reader has to compare to today by eye.
 *
 * EDITING HAPPENS IN THE GRID. The specification is explicit that an editable cell is a property of
 * the cell rather than a mode of the page: there is no separate edit form, and the affordance is on
 * the row. So the level and expiry are edited where they are read, and each change is raised on its
 * own rather than accumulated behind a Save — a trace flag is a small, immediate act, and batching
 * it would invent a draft state for a thing that has none.
 *
 * "NO FLAGS" IS THE DANGEROUS EMPTY STATE, not a neutral one: it means nothing is being recorded,
 * which is exactly what somebody about to reproduce a problem needs to know. So it is said in those
 * words rather than as a blank table.
 *
 * THE CLOCK IS AN INPUT. `now` is supplied rather than read from `Date.now()`, so what this
 * component says about expiry is a function of its inputs — testable, and consistent with whatever
 * the surrounding surface is using as its own idea of now.
 *
 * Styling seams: `::part(control)`, `::part(summary)`, `::part(search)`, `::part(table)`,
 * `::part(level)`, `::part(expires)`, `::part(state)`, `::part(remove)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import './data-table.js';
import type { CaosDataTable, CaosSortDirection, CaosTableRow } from './data-table.js';

/** The logging levels a trace flag can be set to, quietest first. */
export const TRACE_LEVELS = ['NONE', 'ERROR', 'WARN', 'INFO', 'DEBUG', 'FINE', 'FINEST'] as const;
export type CaosTraceLevel = (typeof TRACE_LEVELS)[number];

/** One trace flag. */
export interface CaosTraceFlag {
  readonly id: string;
  /** Who or what is being traced. */
  readonly subject: string;
  /** What kind of thing the subject is — `User`, `Class`, `Trigger`. */
  readonly subjectType?: string;
  readonly level: CaosTraceLevel;
  /** When it stops recording, as an ISO timestamp. ABSENT ⇒ no expiry was stated, which is not "never". */
  readonly expiresAt?: string;
}

const sheet = makeSheet(`
  :host { display: block; }

  .control { display: flex; flex-direction: column; gap: var(--caos-space-3); }

  .head { display: flex; gap: var(--caos-space-3); align-items: center; justify-content: space-between; flex-wrap: wrap; }

  .summary { font-family: var(--caos-font); font-size: 12.5px; color: var(--caos-color-text-muted); }
  .summary strong { color: var(--caos-color-text); font-weight: 600; }
  .summary .expired { color: var(--caos-color-warning-text); font-weight: 600; }
  .summary .none { color: var(--caos-color-danger-text); font-weight: 600; }

  .search {
    flex: 0 1 220px;
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-surface);
    color: var(--caos-color-text);
    font-family: var(--caos-font);
    font-size: 12.5px;
    padding: 5px 9px;
  }
  .search:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }

  .empty { font-family: var(--caos-font); font-size: 13px; color: var(--caos-color-danger-text); }
`);

/** Whether a flag is still recording, given an idea of now. No expiry stated ⇒ it cannot be judged. */
export function traceFlagState(flag: CaosTraceFlag, now: number): 'active' | 'expired' | 'unstated' {
  if (flag.expiresAt === undefined) return 'unstated';
  const at = Date.parse(flag.expiresAt);
  if (Number.isNaN(at)) return 'unstated';
  return at > now ? 'active' : 'expired';
}

export class CaosTraceFlagControl extends CaosElement {
  static get observedAttributes(): string[] {
    return ['search', 'now', 'sort-key', 'sort-direction', 'empty-text', 'readonly'];
  }

  private summaryEl: HTMLParagraphElement;
  private searchEl: HTMLInputElement;
  private tableEl: CaosDataTable;
  private emptyEl: HTMLParagraphElement;

  private data: readonly CaosTraceFlag[] = [];

  constructor() {
    super(sheet);

    const wrap = document.createElement('div');
    wrap.className = 'control';
    wrap.setAttribute('part', 'control');

    const head = document.createElement('div');
    head.className = 'head';

    this.summaryEl = document.createElement('p');
    this.summaryEl.className = 'summary';
    this.summaryEl.setAttribute('part', 'summary');
    this.summaryEl.setAttribute('role', 'status');

    this.searchEl = document.createElement('input');
    this.searchEl.type = 'search';
    this.searchEl.className = 'search';
    this.searchEl.setAttribute('part', 'search');
    this.searchEl.placeholder = 'Search trace flags';
    this.searchEl.setAttribute('aria-label', 'Search trace flags by subject or level');
    this.searchEl.addEventListener('input', () => this.setAttribute('search', this.searchEl.value));

    head.append(this.summaryEl, this.searchEl);

    this.emptyEl = document.createElement('p');
    this.emptyEl.className = 'empty';
    this.emptyEl.hidden = true;

    this.tableEl = document.createElement('caos-data-table') as CaosDataTable;
    this.tableEl.setAttribute('part', 'table');
    this.tableEl.addEventListener('caos-sort', (e) => {
      const { column, direction } = (e as CustomEvent<{ column: string; direction: CaosSortDirection }>).detail;
      e.stopPropagation();
      this.setAttribute('sort-key', column);
      this.setAttribute('sort-direction', direction);
    });

    wrap.append(head, this.emptyEl, this.tableEl);
    this.root.append(wrap);
  }

  connectedCallback(): void {
    this.render();
  }

  attributeChangedCallback(name: string, _before: string | null, after: string | null): void {
    if (name === 'search' && this.searchEl.value !== (after ?? '')) this.searchEl.value = after ?? '';
    this.render();
  }

  get flags(): readonly CaosTraceFlag[] {
    return this.data;
  }

  set flags(next: readonly CaosTraceFlag[]) {
    this.data = Array.isArray(next) ? next : [];
    this.render();
  }

  /**
   * The instant expiry is judged against.
   *
   * An INPUT rather than a call to `Date.now()`, so what this component says about expiry is a
   * function of what it was given — testable, and consistent with whatever the surrounding surface
   * is using as its own idea of now. Absent falls back to the real clock, which is what an ordinary
   * placement wants.
   */
  get now(): number {
    const asked = this.getAttribute('now');
    if (asked === null) return Date.now();
    const parsed = Number.isNaN(Number(asked)) ? Date.parse(asked) : Number(asked);
    return Number.isNaN(parsed) ? Date.now() : parsed;
  }

  /** The flags still recording. */
  get active(): readonly CaosTraceFlag[] {
    const now = this.now;
    return this.data.filter((f) => traceFlagState(f, now) === 'active');
  }

  /** The flags that have stopped recording. Separate from active, never counted with it. */
  get expired(): readonly CaosTraceFlag[] {
    const now = this.now;
    return this.data.filter((f) => traceFlagState(f, now) === 'expired');
  }

  /** The flags surviving the search, in the current order. */
  get visibleFlags(): readonly CaosTraceFlag[] {
    const term = (this.getAttribute('search') ?? '').trim().toLowerCase();
    const kept =
      term === ''
        ? this.data
        : this.data.filter((f) =>
            [f.subject, f.subjectType, f.level]
              .filter((v): v is string => typeof v === 'string')
              .some((v) => v.toLowerCase().includes(term)),
          );
    const key = this.getAttribute('sort-key') ?? 'subject';
    const sign = this.getAttribute('sort-direction') === 'desc' ? -1 : 1;
    return [...kept].sort((a, b) => {
      if (key === 'expiresAt') {
        // A flag with no stated expiry sorts last in both directions: it is not the soonest to go
        // and it is not the furthest away, it is unknown.
        const x = a.expiresAt === undefined ? undefined : Date.parse(a.expiresAt);
        const y = b.expiresAt === undefined ? undefined : Date.parse(b.expiresAt);
        if (x === undefined && y === undefined) return a.subject.localeCompare(b.subject);
        if (x === undefined || Number.isNaN(x)) return 1;
        if (y === undefined || Number.isNaN(y)) return -1;
        if (x !== y) return (x - y) * sign;
        return a.subject.localeCompare(b.subject);
      }
      const read = (f: CaosTraceFlag): string => String((f as unknown as Record<string, unknown>)[key] ?? '');
      const cmp = read(a).localeCompare(read(b), undefined, { numeric: true, sensitivity: 'base' });
      return (cmp !== 0 ? cmp : a.subject.localeCompare(b.subject)) * sign;
    });
  }

  private change(flag: CaosTraceFlag, patch: Partial<CaosTraceFlag>): void {
    // Raised on its own rather than accumulated behind a Save. A trace flag is a small, immediate
    // act; batching would invent a draft state for a thing that has none.
    this.dispatchEvent(
      new CustomEvent('caos-trace-change', { bubbles: true, composed: true, detail: { id: flag.id, ...patch } }),
    );
  }

  /**
   * NOT called `remove`: `HTMLElement.remove()` already exists, and shadowing it would mean that
   * taking this component off the page called for a trace flag to be deleted instead.
   */
  private requestRemove(flag: CaosTraceFlag): void {
    this.dispatchEvent(new CustomEvent('caos-trace-remove', { bubbles: true, composed: true, detail: { id: flag.id } }));
  }

  private render(): void {
    const visible = this.visibleFlags;
    const nothing = this.data.length === 0;
    this.emptyEl.hidden = !nothing;
    this.tableEl.hidden = visible.length === 0;
    // Not a neutral empty state: no flags means nothing is being recorded, which is exactly what
    // somebody about to reproduce a problem needs to know.
    this.emptyEl.textContent = this.getAttribute('empty-text') ?? 'No trace flags — nothing is being recorded.';

    const nodes: Node[] = [];
    if (nothing) {
      const none = document.createElement('strong');
      none.className = 'none';
      none.textContent = 'Nothing is being recorded';
      nodes.push(none);
    } else {
      const strong = document.createElement('strong');
      const active = this.active.length;
      strong.textContent = `${active} ${active === 1 ? 'flag' : 'flags'} recording`;
      nodes.push(strong);
      const expired = this.expired.length;
      if (expired > 0) {
        nodes.push(document.createTextNode(' · '));
        const e = document.createElement('span');
        e.className = 'expired';
        e.textContent = `${expired} expired`;
        nodes.push(e);
      }
      const unstated = this.data.length - active - expired;
      if (unstated > 0) nodes.push(document.createTextNode(` · ${unstated} with no expiry stated`));
      if (visible.length !== this.data.length) {
        nodes.push(document.createTextNode(` · showing ${visible.length} of ${this.data.length}`));
      }
    }
    this.summaryEl.replaceChildren(...nodes);

    if (visible.length === 0) return;

    this.tableEl.columns = [
      { key: 'subject', label: 'Traced', sortable: true, width: '30%' },
      { key: 'level', label: 'Level', sortable: true, width: '18%' },
      { key: 'expiresAt', label: 'Expires', sortable: true, width: '22%' },
      { key: 'state', label: 'State', sortable: false, width: '18%' },
      { key: 'remove', label: '', width: '12%' },
    ];
    this.tableEl.sort = {
      key: this.getAttribute('sort-key') ?? 'subject',
      direction: this.getAttribute('sort-direction') === 'desc' ? 'desc' : 'asc',
    };
    this.tableEl.rows = visible.map((f) => this.rowFor(f));
  }

  private rowFor(flag: CaosTraceFlag): CaosTableRow {
    const readonly = this.hasAttribute('readonly');
    const state = traceFlagState(flag, this.now);

    const subject = flag.subjectType ? `${flag.subject} (${flag.subjectType})` : flag.subject;

    let levelCell: unknown;
    if (readonly) {
      levelCell = flag.level;
    } else {
      const select = document.createElement('select');
      select.className = 'caos-table__cell-edit';
      select.setAttribute('part', 'level');
      select.setAttribute('aria-label', `Logging level for ${subject}`);
      for (const level of TRACE_LEVELS) {
        const o = document.createElement('option');
        o.value = level;
        o.textContent = level;
        o.selected = level === flag.level;
        select.append(o);
      }
      select.addEventListener('change', () => this.change(flag, { level: select.value as CaosTraceLevel }));
      levelCell = { node: select };
    }

    let expiresCell: unknown;
    if (readonly) {
      expiresCell = flag.expiresAt ?? '';
    } else {
      const input = document.createElement('input');
      input.type = 'datetime-local';
      input.className = 'caos-table__cell-edit';
      input.setAttribute('part', 'expires');
      input.setAttribute('aria-label', `Expiry for ${subject}`);
      // `datetime-local` wants no zone and no seconds; the stored value keeps its own form.
      input.value = flag.expiresAt ? flag.expiresAt.slice(0, 16) : '';
      input.addEventListener('change', () => this.change(flag, { expiresAt: input.value }));
      expiresCell = { node: input };
    }

    const stateEl = document.createElement('span');
    stateEl.setAttribute('part', 'state');
    if (state === 'active') {
      stateEl.textContent = 'recording';
    } else if (state === 'expired') {
      // In words. A date the reader has to compare to today by eye is how somebody reproduces a
      // problem three times and gets no log.
      stateEl.textContent = 'expired — not recording';
      stateEl.className = 'caos-table__cell-note';
    } else {
      stateEl.textContent = 'no expiry stated';
      stateEl.className = 'caos-table__cell-note';
    }

    const removeCell = document.createElement('button');
    removeCell.type = 'button';
    removeCell.className = 'caos-table__idlink';
    removeCell.setAttribute('part', 'remove');
    removeCell.textContent = 'Remove';
    removeCell.setAttribute('aria-label', `Remove the trace flag on ${subject}`);
    removeCell.addEventListener('click', () => this.requestRemove(flag));

    return {
      id: flag.id,
      cells: {
        subject,
        level: levelCell,
        expiresAt: expiresCell,
        state: { node: stateEl },
        remove: readonly ? '' : { node: removeCell },
      },
    } as CaosTableRow;
  }
}

define('caos-trace-flag-control', CaosTraceFlagControl);

declare global {
  interface HTMLElementTagNameMap {
    'caos-trace-flag-control': CaosTraceFlagControl;
  }
}
